import { createSingleNodeInstance, extractAllPaths } from '@glueapp/graph-nodes';
import type { UnpackSelection, UnpackSelections } from '@glueapp/graph-nodes';
import type { MergeInGraphFn } from '@/GraphEditor/drawingLayer/contexts/MergeInGraphContext';
import type { DataConnection, DataType, NodeInstance, NodeInstanceState, ShapeValue, Vector } from '@glueapp/graph-types';

// Wide enough to clear the type-display popover (roughly 200px on average,
// depending on the port's type tree) so the created node isn't hidden behind it.
const HORIZONTAL_GAP = 280;
const DEFAULT_NODE_WIDTH = 200;
const NEW_NODE_ASSUMED_WIDTH = 320;

interface CreateUnpackArgs {
    mergeInGraph: MergeInGraphFn;
    sourceNode: NodeInstance;
    sourcePortName: string;
    sourcePortType: DataType;
    selectedPath?: string;
}

/**
 * Builds the selection entry for a single dot-path, mirroring the in-node UI
 * default: `flat-list` mode when the path sits under any array ancestor,
 * otherwise an empty selection. Returns undefined if the path isn't reachable
 * in the type.
 */
export function buildUnpackSelectionForPath(sourcePortType: DataType, selectedPath: string): UnpackSelection | undefined {
    const pathInfo = extractAllPaths(sourcePortType).find(info => info.dotPath === selectedPath);
    if (!pathInfo) return undefined;
    return pathInfo.listAncestorPaths.length > 0 ? { listMode: 'flat-list' } : {};
}

/**
 * Creates a new Unpack node to the right of `sourceNode` and wires
 * `sourceNode.<sourcePortName>` → the Unpack node's "Input" port.
 *
 * When `selectedPath` is provided, the Unpack node's state is pre-populated so
 * only that dot-path is selected. Otherwise the node starts with empty
 * selections and the user configures it manually.
 *
 * Returns the id assigned to the created Unpack node (or `undefined` if the
 * merge context is unavailable / mergeInGraph didn't return an id).
 */
export function createUnpackFromOutPort({
    mergeInGraph,
    sourceNode,
    sourcePortName,
    sourcePortType,
    selectedPath,
}: CreateUnpackArgs): number | undefined {
    const sourceWidth = sourceNode.width ?? DEFAULT_NODE_WIDTH;
    const position: Vector = {
        x: sourceNode.position.x + sourceWidth + HORIZONTAL_GAP,
        y: sourceNode.position.y,
    };
    const placeholderId = -1;
    const unpackNode = createSingleNodeInstance(placeholderId, 'Unpack', position, false, {});

    let initialSelections: UnpackSelections | undefined;
    if (selectedPath !== undefined) {
        const selection = buildUnpackSelectionForPath(sourcePortType, selectedPath);
        if (selection) {
            initialSelections = { [selectedPath]: selection };
            unpackNode.state = { selections: initialSelections, expanded: true } as unknown as NodeInstanceState;
        }
    }

    const connection: DataConnection = {
        id: -1,
        sendingNodeInstanceId: sourceNode.id,
        sendingPortName: sourcePortName,
        sendingPortType: 'external',
        receivingNodeInstanceId: placeholderId,
        receivingPortName: 'Input',
        receivingPortType: 'external',
    };

    const assignedIds = mergeInGraph([unpackNode], [connection], false);
    return assignedIds[0];
}

interface CreateObjectArgs {
    mergeInGraph: MergeInGraphFn;
    targetNode: NodeInstance;
    targetPortName: string;
    targetObjectType: DataType;
}

/**
 * Creates a new Object construction node to the left of `targetNode` with its
 * "Object Shape" port pre-populated to match the target inport's object type,
 * then wires Object."Created Object" → targetNode.<targetPortName>.
 */
export function createObjectForInPort({ mergeInGraph, targetNode, targetPortName, targetObjectType }: CreateObjectArgs): void {
    const position: Vector = {
        x: targetNode.position.x - NEW_NODE_ASSUMED_WIDTH - HORIZONTAL_GAP,
        y: targetNode.position.y,
    };
    const shapeValue: ShapeValue = {
        isShapeValue: true,
        type: structuredClone(targetObjectType),
    };
    const placeholderId = -1;
    const objectNode = createSingleNodeInstance(placeholderId, 'Object', position, false, {
        'Object Shape': shapeValue,
    });

    const connection: DataConnection = {
        id: -1,
        sendingNodeInstanceId: placeholderId,
        sendingPortName: 'Created Object',
        sendingPortType: 'external',
        receivingNodeInstanceId: targetNode.id,
        receivingPortName: targetPortName,
        receivingPortType: 'external',
    };

    mergeInGraph([objectNode], [connection], false);
}
